import {
  Col,
  Row,
  Spin,
  Table,
  Tag,
  Image as AntImage,
  Progress,
  Typography,
} from "antd";
import ExportProfilePage from "./ExportProfilePage";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { Charts, ProgressBar } from "shared/components";
import { Trans, useTranslation } from "react-i18next";
import { ColumnsType } from "antd/lib/table";
import moment from "moment";
import { getI18nGenre } from "shared/helpers/i18nGenre";
import { useSelector } from "react-redux";
import { ReactComponent as RocketIcon } from "shared/assets/images/rocket-icon.svg";
import { ReactComponent as FollowersIcon } from "shared/assets/images/followers-icon.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDown,
  faArrowTrendUp,
  faArrowUp,
  faUserPlus,
} from "@fortawesome/free-solid-svg-icons";
import { LoadingOutlined } from "@ant-design/icons";
import { useEffect } from "react";
import { getI18nCoutry } from "shared/helpers/i18nCountry";
import CardHeader from "shared/components/card/CardHeader";
import { historyTrackColors, socialmediaColorsValue } from "../ArtistProfile";
import { faSpotify } from "@fortawesome/free-brands-svg-icons";
import { YoutubeFilled, InstagramOutlined } from "@ant-design/icons";
import { ptBRFormat } from "shared/helpers/BRLFormat";

interface ExportProfileProps {
  artist: any;
  artistImage?: string;
  releaseChunks: any[];
  performanceIndexMetricsChart: any;
  audioFeatures: any;
  similarityRank: any;
  trendReleaseInfo: any;
  trendTrackInfo: any;
  // latam: boolean;
  rankFilter?: any;
  artistDetail?: any;
  mostAudiencedCities?: any;
  firstRelease?: any;
  latestRelease?: any;
  daysOfRelease?: any;
  percentageOfRelease?: any;
  average?: any;
  isUnderAverage?: any;
  tracksPopularityHistory?: any;
  close: () => void;
}

const similarityColors = ["#5779FF", "#EC0190", "#E4BC5C", "#FD6F8E"];
const formatByPrecision = (precision: string) => {
  if (precision === "day") return "DD/MM/YYYY";
  if (precision === "month") return "MM/YYYY";
  if (precision === "year") return "YYYY";
};

const htmlToCanvasToPdf = async (
  fileName: string = "profile",
  callback: () => void
) => {
  const doc = new jsPDF("p", "mm", "a4");

  const width = doc.internal.pageSize.getWidth();
  const height = doc.internal.pageSize.getHeight();
  const artistProfile = document.getElementById("artist-profile");
  const catalogAnalysis1 = document.getElementById("catalog-analysis-1");
  const catalogAnalysis2 = document.getElementById("catalog-analysis-2");
  const releasePages = document.getElementsByClassName("release-page");

  const audioFeature = document.getElementById("audio-feature");
  if (
    artistProfile &&
    catalogAnalysis1 &&
    catalogAnalysis2 &&
    releasePages.length > 0 &&
    audioFeature
  ) {
    const artistProfilePage = await html2canvas(artistProfile, {
      imageTimeout: 2000,
    });

    const catalogAnalysis1Page = await html2canvas(catalogAnalysis1);
    const catalogAnalysis2PAge = await html2canvas(catalogAnalysis2);

    const releasesPages = await Promise.all(
      Array.from(releasePages).map(
        async (collection: any) => await html2canvas(collection)
      )
    );

    const audioFeaturePage = await html2canvas(audioFeature);

    doc.addImage(artistProfilePage, "MPEG", 0, 0, width, height);
    doc.addPage();
    doc.addImage(catalogAnalysis1Page, "MPEG", 0, 0, width, height);
    doc.addPage();
    doc.addImage(catalogAnalysis2PAge, "MPEG", 0, 0, width, height);
    doc.addPage();
    releasesPages.forEach((page) => {
      doc.addImage(page, "MPEG", 0, 0, width, height);
      doc.addPage();
    });
    doc.addImage(audioFeaturePage, "MPEG", 0, 0, width, height);

    doc.save(`${fileName}.pdf`);
    callback();
  }
};

const ExportProfile: React.FC<ExportProfileProps> = (props) => {
  const {
    artist,
    artistImage,
    performanceIndexMetricsChart,
    releaseChunks,
    audioFeatures,
    similarityRank,
    trendReleaseInfo,
    trendTrackInfo,
    // latam,
    rankFilter,
    artistDetail,
    firstRelease,
    latestRelease,
    daysOfRelease,
    percentageOfRelease,
    average,
    isUnderAverage,
    tracksPopularityHistory,
    close,
    mostAudiencedCities,
  } = props;

  const { genres } = useSelector((state: any) => state.genres);
  const { countries } = useSelector((state: any) => state.countries);
  const { t, i18n } = useTranslation("radar");
  const artistName = artist.artistMetadata.artistName;
  const wrappGetI18nGenre = (genre: string) =>
    getI18nGenre(genre, i18n, genres);
  const wrappGetI18nCountry = (country: string) =>
    getI18nCoutry(country, i18n, countries);

  const colunas: ColumnsType<any> = [
    { title: t("pages.ArtistProfile.releaseName"), dataIndex: "albumName" },
    {
      title: t("pages.ArtistProfile.releaseDate"),
      dataIndex: "releaseDate",
      render: (value) => (
        <>
          {moment(value?.dateTruncated).format(
            formatByPrecision(value.precision)
          )}
        </>
      ),
    },
    {
      title: t("pages.ArtistProfile.releaseType"),
      dataIndex: "albumType",
    },
    {
      title: t("pages.ArtistProfile.releaseLabel"),
      dataIndex: "label",
    },
    {
      title: t("pages.ArtistProfile.popularity"),
      dataIndex: "popularity",
      align: "center",
    },
  ];

  useEffect(() => {
    const fileName = `${artistName.replace(/\s/g, "_")}_STRM_REPORT `;
    htmlToCanvasToPdf(fileName, () => {
      close();
    });
  }, [artistName, close]);

  const antIcon = (
    <LoadingOutlined
      style={{ fontSize: 48, color: "#ececec" }}
      spin
      className="spin-ref"
    />
  );

  const getfilterPlus = (filterArray: any, limit: number, label: string) => {
    if (filterArray && filterArray.length > 0) {
      if (filterArray.length > limit) {
        const sliced = filterArray.slice(0, limit);
        return `${sliced.join(", ")}. (+${
          filterArray.length - limit
        } ${label})`;
      } else {
        return `${filterArray.join(", ")}.`;
      }
    }
    return "-";
  };

  return (
    <Spin
      spinning={true}
      tip={t("pages.ArtistProfile.loadingMessage")}
      indicator={antIcon}
    >
      <div style={{ margin: 0 }}>
        <ExportProfilePage
          id="artist-profile"
          page={1}
          title={t("pages.ArtistProfile.title")}
          className="profile"
        >
          <Row gutter={[16, 32]}>
            <Col xs={24}>
              <Row gutter={32}>
                <Col
                  xs={8}
                  style={{
                    textAlign: "left",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Row>
                    <div className="artist-info-box">
                      <AntImage
                        src={artistImage}
                        style={{
                          borderRadius: "50%",
                          width: 75,
                          height: 75,
                          objectFit: "cover",
                        }}
                      />
                      <div className="name">
                        {artist?.artistMetadata?.artistName}

                        <span>
                          {wrappGetI18nCountry(
                            artist?.artistMetadata?.country
                          ).toUpperCase()}
                        </span>
                      </div>
                    </div>
                  </Row>
                  <Row style={{ flex: 1 }}>
                    <div className="artist-infos">
                      <div>
                        <span className="genre">
                          {t("pages.ArtistProfile.genres")}
                        </span>
                        {artist?.artistMetadata?.genresStrm?.map(
                          (genre: any) => (
                            <Tag
                              key={Math.random()}
                              style={{
                                border: "1px solid #E6E6E633",
                                color: "#E6E6E6",
                                backgroundColor: "#E6E6E611",
                                textTransform: "capitalize",
                              }}
                            >
                              {wrappGetI18nGenre(genre)}
                            </Tag>
                          )
                        )}
                        <span className="genre" style={{ marginTop: 16 }}>
                          {t("pages.ArtistProfile.spotifyGenres")}
                        </span>
                        {artist?.spotify?.genresSpotify?.map((genre: any) => (
                          <Tag
                            key={Math.random()}
                            style={{
                              border: "1px solid #E6E6E633",
                              color: "#E6E6E6",
                              backgroundColor: "#E6E6E611",
                              textTransform: "capitalize",
                              marginTop: 6,
                            }}
                          >
                            {genre}
                          </Tag>
                        ))}
                      </div>

                      <div>
                        <div>
                          <span className="stage">
                            {t("pages.ArtistProfile.stage")}
                          </span>
                          <span className="stage-name">
                            {t(
                              `pages.CareerDiagnose.stage.${
                                artist?.strmIndexes?.careerStage || 0
                              }.journeyTitle`
                            )}
                          </span>
                        </div>
                        <Progress
                          strokeColor="#75C594"
                          strokeWidth={5}
                          trailColor="#D2CCDF33"
                          percent={Math.ceil(
                            artist?.strmIndexes?.stageProgress * 100 || 0
                          )}
                        />
                      </div>
                    </div>
                  </Row>
                </Col>
                <Col xs={8}>
                  <CardHeader
                    className="without-header"
                    style={{ height: "100%", display: "flex" }}
                  >
                    <Row>
                      <Col xs={24}>
                        <div style={{ height: 70, padding: 12 }}>
                          <h1> {t("pages.ArtistProfile.totalFollowers")}</h1>
                          <h4>
                            {" "}
                            {t(
                              "pages.ArtistProfile.followersAndSubscribersLast30Days"
                            )}
                          </h4>
                        </div>
                        <div className="followers-card">
                          <div
                            style={{
                              display: "flex",
                              marginTop: 24,
                            }}
                          >
                            <Charts.PieChart
                              height={100}
                              width={120}
                              innerRadius={35}
                              outerRadius={45}
                              customLabel={() => <></>}
                              colors={socialmediaColorsValue.map(
                                ({ color }) => color
                              )}
                              series={socialmediaColorsValue.map(
                                ({ text }) => ({
                                  value:
                                    (artistDetail &&
                                      artistDetail[
                                        `${text.toLowerCase()}FollowersTotal`
                                      ]) ||
                                    0,
                                  name: text,
                                })
                              )}
                            />
                            <div>
                              <div style={{ display: "flex" }}>
                                <span
                                  style={{
                                    fontSize: 16,
                                    fontWeight: 600,
                                    color: "#fff",
                                    paddingLeft: 8,
                                  }}
                                >
                                  {ptBRFormat(artistDetail?.followersTotal)}
                                </span>
                                {artistDetail?.followersTotalDifPercentage !==
                                  null && (
                                  <span
                                    style={{
                                      fontSize: 14,
                                      fontWeight: 400,
                                    }}
                                    className={`weekly-change ${
                                      artistDetail?.followersTotalDifPercentage >
                                      0
                                        ? "up"
                                        : "down"
                                    }`}
                                  >
                                    {artistDetail?.followersTotalDifPercentage ===
                                    0 ? (
                                      <></>
                                    ) : artistDetail?.followersTotalDifPercentage >
                                      0 ? (
                                      <FontAwesomeIcon icon={faArrowUp} />
                                    ) : (
                                      <FontAwesomeIcon icon={faArrowDown} />
                                    )}
                                    {artistDetail?.followersTotalDifPercentage}{" "}
                                    %
                                  </span>
                                )}
                              </div>
                              <div className="social-legend">
                                {socialmediaColorsValue.map((info: any) => (
                                  <div className="item">
                                    <span className="label">
                                      <span
                                        className="circle"
                                        style={{
                                          backgroundColor: info.color,
                                          fontWeight: 400,
                                        }}
                                      />
                                      {info.text}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </Col>
                      <Col xs={24}>
                        <div style={{ padding: 16 }}>
                          <Row>
                            <Col xs={24}>
                              <div className="card-social">
                                <div className="artist-profile">
                                  <div className="platform-icon">
                                    <span>
                                      <FontAwesomeIcon
                                        icon={faSpotify}
                                        color="#F2F2F2"
                                      />
                                    </span>
                                    <span style={{ color: "#c8c8c8" }}>
                                      Spotify
                                    </span>
                                  </div>
                                  <span className="platform-number">
                                    {ptBRFormat(
                                      artistDetail?.spotifyFollowersTotal
                                    )}
                                  </span>
                                </div>

                                <div className="artist-profile">
                                  <div className="platform-icon">
                                    <span>
                                      <InstagramOutlined />
                                    </span>
                                    <span style={{ color: "#c8c8c8" }}>
                                      Instagram
                                    </span>
                                  </div>
                                  <span className="platform-number">
                                    {ptBRFormat(
                                      artistDetail?.instagramFollowersTotal
                                    )}
                                  </span>
                                </div>
                                <div className="artist-profile">
                                  <div className="platform-icon">
                                    <span>
                                      <YoutubeFilled />
                                    </span>
                                    <span style={{ color: "#c8c8c8" }}>
                                      Youtube
                                    </span>
                                  </div>
                                  <span className="platform-number">
                                    {ptBRFormat(
                                      artistDetail?.youtubeFollowersTotal
                                    )}
                                  </span>
                                </div>
                              </div>
                            </Col>
                          </Row>
                        </div>
                      </Col>
                    </Row>
                  </CardHeader>
                </Col>
                <Col
                  xs={8}
                  style={{ display: "flex", gap: 8, flexDirection: "column" }}
                >
                  <CardHeader className="without-header">
                    <div className="general-performance">
                      <div>
                        <span
                          className={`weekly-change ${
                            rankFilter?.positionDif === 0
                              ? "same"
                              : rankFilter?.positionDif > 0
                              ? "up"
                              : "down"
                          }`}
                        >
                          {rankFilter?.positionDif === 0 ? (
                            <></>
                          ) : rankFilter?.positionDif > 0 ? (
                            <FontAwesomeIcon icon={faArrowUp} />
                          ) : (
                            <FontAwesomeIcon icon={faArrowDown} />
                          )}{" "}
                          {rankFilter?.positionDif > 0 ||
                          rankFilter?.positionDif < 0
                            ? Math.abs(rankFilter?.positionDif)
                            : "-"}
                        </span>
                        <span>{t("pages.ArtistProfile.weeklyChanges")}</span>
                      </div>
                      <div>
                        <span className="variation">
                          # {rankFilter?.performanceIndexRank}
                        </span>
                        <span>
                          {t("pages.ArtistProfile.generalPerformanceRanking")}
                        </span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardHeader className="without-header">
                    <div className="card-engagement">
                      <span>
                        <FontAwesomeIcon
                          icon={faArrowTrendUp}
                          color="#5DD183"
                        />{" "}
                        {t("pages.ArtistProfile.engagement")}
                      </span>
                      <span>
                        {" "}
                        <FontAwesomeIcon
                          icon={faUserPlus}
                          color="#5DD183"
                        />{" "}
                        {t("pages.ArtistProfile.followers")}
                      </span>
                      <span
                        className={`change ${
                          artistDetail?.engagementDif > 0 ? "up" : "down"
                        }`}
                      >
                        {artistDetail?.engagementDif > 0 ? (
                          <FontAwesomeIcon icon={faArrowUp} />
                        ) : (
                          <FontAwesomeIcon icon={faArrowDown} />
                        )}
                        {` ${Math.abs(artistDetail?.engagementDif)}% `}
                        {artistDetail?.engagementDif < 0
                          ? t("pages.ArtistProfile.belowAverage")
                          : t("pages.ArtistProfile.aboveAverage")}
                      </span>
                      <span
                        className={`change ${
                          artistDetail?.followersDif > 0 ? "up" : "down"
                        }`}
                      >
                        {artistDetail?.followersDif > 0 ? (
                          <FontAwesomeIcon icon={faArrowUp} />
                        ) : (
                          <FontAwesomeIcon icon={faArrowDown} />
                        )}
                        {` ${Math.abs(artistDetail?.followersDif)}% `}
                        {artistDetail?.followersDif < 0
                          ? t("pages.ArtistProfile.belowAverage")
                          : t("pages.ArtistProfile.aboveAverage")}
                      </span>
                    </div>
                  </CardHeader>
                  <CardHeader className="without-header">
                    <div style={{ padding: 16 }}>
                      <Typography.Title
                        style={{ fontSize: 16, marginBottom: 20 }}
                      >
                        <FontAwesomeIcon
                          icon={faSpotify}
                          style={{ marginRight: 12 }}
                          color=" #44DE66"
                        />{" "}
                        {t("pages.ArtistProfile.biggestAudiences")}
                      </Typography.Title>
                      <div className="most-audienced-city">
                        <span
                          style={{
                            marginLeft: 33,
                            fontSize: 12,
                            fontWeight: 400,
                          }}
                        >
                          {t("pages.ArtistProfile.city")}
                        </span>
                        <span> {t("pages.ArtistProfile.listeners")}</span>
                      </div>
                      <div className="most-audienced-cities">
                        {mostAudiencedCities.map((item: any, index: any) => {
                          return (
                            <div
                              className="most-audienced-city"
                              key={`audiencie_${index}`}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <span className="item-list-circle">
                                  {index + 1}
                                </span>
                                <span
                                  style={{
                                    marginLeft: "15px",
                                  }}
                                >
                                  {item.city}
                                </span>
                              </div>

                              {`${item.listenerCount}`}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </CardHeader>
                </Col>
              </Row>
              <h1
                className="page-title-new"
                style={{
                  marginBottom: 0,
                  margin: "32px auto 0 auto",
                  maxWidth: "400px",
                  textAlign: "center",
                  color: "white",
                  fontWeight: "bold",
                  lineHeight: "100%",
                  fontSize: 20,
                }}
              >
                {t("pages.Radar.graphicGeneralPerformance")}
              </h1>

              {performanceIndexMetricsChart.categories.length === 0 && (
                <div className="no-data-container" style={{ top: 600 }}>
                  <div className="no-data-text">
                    {t("pages.ArtistProfile.noPerfomanceData")}
                  </div>
                </div>
              )}
              <Row justify="center" className="chart">
                <Col xs={20} style={{ width: "100%" }}>
                  <Charts.AreaChart
                    color={["#5DD183"]}
                    strokeWidth={1}
                    markerSize={6}
                    showAnnotationMonths
                    textColor="#999999"
                    xaxisLabelFormat="MMM-yyyy"
                    xaxisType="datetime"
                    {...performanceIndexMetricsChart}
                    xAxisFormatter={(val) => val.toLocaleString()}
                    height={400}
                    animation={false}
                  />
                </Col>
              </Row>
              <Row
                className="disclaimer-new"
                justify="center"
                style={{ marginTop: 32 }}
              >
                <Col style={{ maxWidth: "600px" }}>
                  {t("pages.ArtistProfile.disclaimer")}
                </Col>
              </Row>
              <Row gutter={[16, 32]} justify="center">
                <Col xs={24}>
                  <h1 className="page-title-new">
                    {t("pages.ArtistProfile.strmIndex")}
                  </h1>
                  <ProgressBar
                    value={(
                      artist?.strmIndexes?.performanceIndex || 0
                    ).toPrecision(3)}
                    format={(value) => `${value} %`}
                    color="#36BFFA"
                    title={t("pages.ArtistProfile.performanceChart")}
                    titleClass="texto-maiusculo"
                    strokeWidth={12}
                  />
                  <span className="index-explaination">
                    {t("pages.ArtistProfile.performanceExplaination")}
                  </span>
                  <ProgressBar
                    value={(
                      artist?.strmIndexes?.developmentIndex || 0
                    ).toPrecision(3)}
                    format={(value) => `${value} %`}
                    color="#F670C7"
                    title={t("pages.ArtistProfile.developmentChart")}
                    titleClass="texto-maiusculo"
                    strokeWidth={12}
                  />
                  <span className="index-explaination">
                    {t("pages.ArtistProfile.developmentExplaination")}
                  </span>
                  <ProgressBar
                    value={(
                      artist?.strmIndexes?.performanceSpotify || 0
                    ).toPrecision(3)}
                    format={(value) => `${value} %`}
                    color="#F97066"
                    title={t("pages.ArtistProfile.audioChart")}
                    titleClass="texto-maiusculo"
                    strokeWidth={12}
                  />
                  <span className="index-explaination">
                    {t("pages.ArtistProfile.performanceSpotifyExplaination")}
                  </span>
                  <ProgressBar
                    value={(
                      artist?.strmIndexes?.performanceInstagram || 0
                    ).toPrecision(3)}
                    format={(value) => `${value} %`}
                    color="#32D583"
                    title={t("pages.ArtistProfile.imageChart")}
                    titleClass="texto-maiusculo"
                    strokeWidth={12}
                  />
                  <span className="index-explaination">
                    {t("pages.ArtistProfile.performanceInstagramExplaination")}
                  </span>
                  <ProgressBar
                    value={(
                      artist?.strmIndexes?.volatilityIndex || 0
                    ).toPrecision(3)}
                    format={(value) => `${value} %`}
                    color="#FDB022"
                    title={t("pages.ArtistProfile.volatilityChart")}
                    titleClass="texto-maiusculo"
                    strokeWidth={12}
                  />
                  <span className="index-explaination">
                    {t("pages.ArtistProfile.volatilityExplaination")}
                  </span>
                </Col>
              </Row>
              <Row style={{ textAlign: "end", marginTop: 32 }}>
                <Col xs={24}>
                  <Row justify="end">
                    <span style={{ marginRight: 12 }}>
                      {t("pages.ArtistProfile.filterTextExport")}
                    </span>
                    <span>
                      <strong>{t("pages.ArtistProfile.genreFlter")}</strong>
                      {getfilterPlus(
                        rankFilter?.filter?.genre,
                        3,
                        t("pages.ArtistProfile.genresFilter")
                      )}
                    </span>
                  </Row>
                  <Row justify="end">
                    <span style={{ marginRight: 12 }}>
                      <strong>{t("pages.ArtistProfile.countryFilter")}</strong>
                      {getfilterPlus(
                        rankFilter?.filter?.country,
                        3,
                        t("pages.ArtistProfile.countriesFilter")
                      )}
                    </span>
                    <span style={{ marginRight: 12 }}>
                      <strong>{t("pages.TracksRanking.stage")}: </strong>
                      {rankFilter?.filter?.stageInit} -{" "}
                      {rankFilter?.filter?.stageEnd}
                    </span>
                    <span>
                      <strong>
                        {t("pages.ArtistProfile.spotifyGenreFilter")}
                      </strong>
                      {getfilterPlus(
                        rankFilter?.filter?.genreSpotify,
                        3,
                        t("pages.ArtistProfile.genresFilter")
                      )}
                    </span>
                  </Row>
                  <Row justify="end">
                    <span style={{ marginRight: 12 }}>
                      {t("pages.ArtistProfile.totalArtists")}
                      <strong>{rankFilter?.artistCount}</strong>
                    </span>
                    <span style={{ marginRight: 12 }}>
                      <strong>
                        {t("pages.ArtistProfile.daysSinceFirstReleaseFilter")}
                      </strong>
                      {(rankFilter?.filter?.daysSinceFirstRelease &&
                        t("pages.ArtistsRanking.yearsSinceRelease", {
                          year: rankFilter?.filter?.daysSinceFirstRelease / 365,
                        })) ||
                        "-"}
                    </span>
                    <span>
                      <strong>
                        {t("pages.ArtistProfile.releasesLastYearFilter")}
                      </strong>
                      {rankFilter?.filter?.releasesLastYear || "-"}
                    </span>
                  </Row>
                </Col>
              </Row>
            </Col>
          </Row>
        </ExportProfilePage>
        <ExportProfilePage
          id="catalog-analysis-1"
          page={2}
          title={t("pages.ArtistProfile.title")}
          className="profile"
        >
          <h1 className="page-title">
            {t("pages.ArtistProfile.popularityEvolution")}
          </h1>
          <span className="disclaimer">
            {t("pages.ArtistProfile.popularityEvolutionExplaination")}
          </span>
          <Row gutter={[16, 16]}>
            <Col xs={24}>
              <CardHeader
                header={
                  <div style={{ padding: 16 }}>
                    <Typography.Text>
                      {t("pages.ArtistProfile.releasesSingleEpAlbun")}
                    </Typography.Text>
                  </div>
                }
              >
                <div style={{ padding: 16, color: "#fff" }}>
                  <Row>
                    <Col xs={4}>
                      <div className="catalog-info">
                        <label>{t("pages.ArtistProfile.firstRelease")}</label>
                        <span>{firstRelease}</span>
                      </div>
                    </Col>
                    <Col xs={4}>
                      <div className="catalog-info">
                        <label>{t("pages.ArtistProfile.lastRelease")}</label>
                        <span>{latestRelease}</span>
                      </div>
                    </Col>
                    <Col xs={4}>
                      <div className="catalog-info">
                        <label>{t("pages.ArtistProfile.singles")}</label>
                        <span>{artistDetail?.totalSingles}</span>
                      </div>
                    </Col>
                    <Col xs={4}>
                      <div className="catalog-info">
                        <label>{t("pages.ArtistProfile.albuns")}</label>
                        <span>{artistDetail?.totalAlbuns}</span>
                      </div>
                    </Col>
                    <Col xs={4}>
                      <div className="catalog-info">
                        <label>{t("pages.ArtistProfile.totalReleases")}</label>
                        <span>{artistDetail?.totalReleases}</span>
                      </div>
                    </Col>
                  </Row>
                </div>
              </CardHeader>
            </Col>
            <Col xs={24}>
              <CardHeader
                header={
                  <div style={{ padding: 16 }}>
                    <Typography.Text>
                      {t("pages.ArtistProfile.featTracks")}
                    </Typography.Text>
                  </div>
                }
              >
                <div style={{ padding: 16, color: "#fff" }}>
                  <Row>
                    <Col xs={4}>
                      <div className="catalog-info">
                        <label>{t("pages.ArtistProfile.leading")}</label>
                        <span>{artistDetail?.protagonistCollab}</span>
                      </div>
                    </Col>
                    <Col xs={4}>
                      <div className="catalog-info">
                        <label>{t("pages.ArtistProfile.guest")}</label>
                        <span>{artistDetail?.invitedCollab}</span>
                      </div>
                    </Col>
                    <Col xs={4}>
                      <div className="catalog-info">
                        <label>{t("pages.ArtistProfile.total")}</label>
                        <span>{artistDetail?.totalCollab}</span>
                      </div>
                    </Col>
                  </Row>
                </div>
              </CardHeader>
            </Col>
            <Col xs={6}>
              <CardHeader>
                <div className="info-card">
                  <div className="info">
                    <span>{t("pages.ArtistProfile.lastTwelveMonths")}</span>
                    <span className="value">
                      {t("pages.ArtistProfile.releasesCount", {
                        releases: artistDetail?.releasesLast12MonthsTotal,
                      })}
                    </span>
                    <div className="variation">
                      <span
                        className={`weekly-change ${
                          !artistDetail?.releaseLast12MonthsIsUnderAverage
                            ? "up"
                            : "down"
                        }`}
                      >
                        {!artistDetail?.releaseLast12MonthsIsUnderAverage ? (
                          <FontAwesomeIcon icon={faArrowUp} />
                        ) : (
                          <FontAwesomeIcon icon={faArrowDown} />
                        )}{" "}
                        {Math.abs(
                          artistDetail?.releaseLast12MonthsPercentage
                        ).toPrecision(3)}{" "}
                        %
                      </span>
                      <span>
                        {artistDetail?.releaseLast12MonthsIsUnderAverage
                          ? t("pages.ArtistProfile.belowAverage")
                          : t("pages.ArtistProfile.aboveAverage")}
                      </span>
                    </div>
                  </div>
                  <div className="icon">
                    <RocketIcon />
                  </div>
                </div>
              </CardHeader>
            </Col>
            <Col xs={6}>
              <CardHeader>
                <div className="info-card">
                  <div className="info">
                    <span>{t("pages.ArtistProfile.releaseFrequency")}</span>
                    <span className="value">
                      <Trans
                        i18nKey="pages.ArtistProfile.releaseFrequencyText"
                        ns="radar"
                        values={{ daysOfRelease }}
                      />
                    </span>
                    <div className="variation">
                      <span
                        className={`weekly-change ${
                          isUnderAverage ? "down" : "up"
                        }`}
                      >
                        {isUnderAverage ? (
                          <FontAwesomeIcon icon={faArrowDown} />
                        ) : (
                          <FontAwesomeIcon icon={faArrowUp} />
                        )}{" "}
                        {percentageOfRelease}
                      </span>
                      <span> {average}</span>
                    </div>
                  </div>
                  <div className="icon">
                    <RocketIcon />
                  </div>
                </div>
              </CardHeader>
            </Col>
            <Col xs={6}>
              <CardHeader>
                <div className="info-card">
                  <div className="info">
                    <span>{t("pages.ArtistProfile.popularity")}</span>
                    <span className="value">
                      <Trans
                        i18nKey="pages.ArtistProfile.feat"
                        ns="radar"
                        values={{
                          feat:
                            artistDetail?.popularityFeat !== null
                              ? artistDetail?.popularityFeat
                              : "-",
                        }}
                      />
                      {` VS `}
                      <Trans
                        i18nKey="pages.ArtistProfile.solo"
                        ns="radar"
                        values={{
                          solo:
                            artistDetail?.popularitySolo !== null
                              ? artistDetail?.popularitySolo
                              : "-",
                        }}
                      />
                    </span>
                    <div className="variation">
                      <span className="weekly-change no-change">
                        {artistDetail?.percentageBetweenPopularityFeatAndPopularitySolo.toPrecision(
                          3
                        )}{" "}
                        %
                      </span>
                      <span>
                        {t("pages.ArtistProfile.percentageDifference")}
                      </span>
                    </div>
                  </div>
                  <div className="icon">
                    <FollowersIcon
                      style={{
                        transform: "matrix(0.98, -0.17, 0.2, 0.98, 0, 0)",
                      }}
                    />
                  </div>
                </div>
              </CardHeader>
            </Col>
            <Col xs={6}>
              <CardHeader>
                <div className="info-card">
                  <div className="info">
                    <span>{t("pages.ArtistProfile.popularity")}</span>
                    <span className="value">
                      <Trans
                        i18nKey="pages.ArtistProfile.single"
                        ns="radar"
                        values={{
                          single:
                            artistDetail?.popularitySingle !== null
                              ? artistDetail?.popularitySingle
                              : "-",
                        }}
                      />
                      {` VS `}
                      <Trans
                        i18nKey="pages.ArtistProfile.album"
                        ns="radar"
                        values={{
                          album:
                            artistDetail?.popularityAlbum !== null
                              ? artistDetail?.popularityAlbum
                              : "-",
                        }}
                      />
                    </span>
                    <div className="variation">
                      <span className="weekly-change no-change">
                        {artistDetail?.percentageBetweenPopularitySingleAndPopularityAlbum.toPrecision(
                          3
                        )}{" "}
                        %
                      </span>
                      <span>
                        {t("pages.ArtistProfile.percentageDifference")}
                      </span>
                    </div>
                  </div>
                  <div className="icon">
                    <FollowersIcon
                      style={{
                        transform: "matrix(0.98, -0.17, 0.2, 0.98, 0, 0)",
                      }}
                    />
                  </div>
                </div>
              </CardHeader>
            </Col>
          </Row>

          <div style={{ marginTop: 72 }}>
            <Charts.ScatterChart
              {...trendReleaseInfo}
              yAxisLabelText={t("pages.ArtistProfile.popularity")}
              xAxisLabelText={t("pages.ArtistProfile.releaseAgeInMonths")}
              customLegendItems={[t("pages.ArtistProfile.releaseTrendLegend")]}
              title={t("pages.ArtistProfile.releasesTrendPopularity")}
              containerHeight={600}
              animation={false}
            />
          </div>
        </ExportProfilePage>
        <ExportProfilePage
          id="catalog-analysis-2"
          page={3}
          artistName={artistName}
          title={t("pages.ArtistProfile.title")}
          className="profile"
        >
          <Charts.ScatterChart
            {...trendTrackInfo}
            yAxisLabelText={t("pages.ArtistProfile.popularity")}
            xAxisLabelText={t("pages.ArtistProfile.trackAgeInMonths")}
            showLegend={false}
            title={t("pages.ArtistProfile.trackPopularityTrend")}
            containerHeight={600}
            animation={false}
          />
          <div
            className="apexcharts-legend apexcharts-align-left apx-legend-position-bottom"
            style={{ marginBottom: 16, display: "flex", gap: 8 }}
          >
            <div className="apexcharts-legend-series">
              <span
                className="apexcharts-legend-marker"
                style={{
                  background: "#3791e3",
                  height: 12,
                  width: 12,
                  borderWidth: 0,
                  borderRadius: 12,
                }}
              ></span>
              <span
                className="apexcharts-legend-text"
                style={{
                  color: "#fff",
                  fontSize: 12,
                }}
              >
                {t("pages.ArtistProfile.trackTrendLgend")}
              </span>
            </div>
            <div className="apexcharts-legend-series">
              <span
                className="apexcharts-legend-marker"
                style={{
                  background: "#EC0190",
                  height: 12,
                  width: 12,
                  borderWidth: 0,
                  borderRadius: 12,
                }}
              ></span>
              <span
                className="apexcharts-legend-text"
                style={{
                  color: "#fff",
                  fontSize: 12,
                }}
              >
                {t("pages.ArtistProfile.trackTrendFeat")}
              </span>
            </div>
          </div>
          {/* <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
              textAlign: "end",
            }}
          >
            <span style={{ width: "50%" }}>
              {t("pages.ArtistProfile.latamDisclaimer")}
            </span>
          </div> */}

          <Charts.AreaChart
            {...tracksPopularityHistory}
            curveType="straight"
            color={historyTrackColors.map((color) => color.color)}
            xaxisLabelFormat="MM/yy"
            xaxisType="datetime"
            xLabelFormatter={(val) => moment(val).format("DD/MM/YYYY")}
            titulo={t("pages.ArtistProfile.trackPopularityHistory")}
            animation={false}
            height={500}
          />
          <Row
            className="disclaimer-new"
            justify="center"
            style={{ marginTop: 32 }}
          >
            <Col style={{ maxWidth: "600px" }}>
              {t("pages.ArtistProfile.disclaimerTrackPopularityHistory")}
            </Col>
          </Row>
        </ExportProfilePage>
        {releaseChunks?.map((chunk, index) => (
          <ExportProfilePage
            id={`release-${index + 1}`}
            page={4 + index}
            artistName={artistName}
            title={t("pages.ArtistProfile.title")}
            className="profile release-page"
          >
            <Row gutter={[16, 32]} justify="center">
              <Col xs={24}>
                <h1 className="page-title">
                  {t("pages.ArtistProfile.releases")}
                </h1>
                <Table
                  columns={colunas}
                  dataSource={chunk}
                  locale={{
                    emptyText: (
                      <span className="empty-table">
                        {t("pages.ArtistProfile.noReleases")}
                      </span>
                    ),
                  }}
                  rowKey="id"
                  pagination={false}
                />
              </Col>
            </Row>
          </ExportProfilePage>
        ))}
        <ExportProfilePage
          id="audio-feature"
          page={4 + (releaseChunks?.length || 1)}
          artistName={artistName}
          title={t("pages.ArtistProfile.title")}
          className="profile"
        >
          <Row gutter={[16, 32]} justify="center">
            <Col xs={24}>
              <h1 className="page-title">
                {t("pages.ArtistProfile.audioFeatures")}
              </h1>
              <Charts.RadarChart
                cor={["#5779FF", "#EC0190", "#E4BC5C", "#FD6F8E"]}
                legend={false}
                textColor="#E6E6E6"
                polygonProps={{
                  strokeColors: "#4F4E59",
                  connectorColors: "#4F4E59",
                  strokeWidth: "1px",
                  fillColors: ["#2d2c30", "#212023"],
                }}
                legendStyle={{
                  horizontalAlign: "left",
                }}
                multiplyBy100
                strokeWidth={0}
                markerSize={9}
                height={650}
                useI18nLabel={false}
                {...audioFeatures}
              />
              <h1 className="page-title">
                {t("pages.ArtistProfile.similarity")}
              </h1>
              <Row justify="center">
                <Col xs={20}>
                  <Row justify="center">
                    <Col xs={12}>
                      <div className="legenda">
                        <div>
                          <span className="label">
                            <span
                              className="circulo"
                              style={{ backgroundColor: "#5779FF" }}
                            >
                              &nbsp;&nbsp;&nbsp;&nbsp;
                            </span>
                            {artistName}
                          </span>
                        </div>
                      </div>
                    </Col>
                  </Row>
                  <Row style={{ marginTop: 32 }}>
                    <Col xs={24}>
                      <div className="legenda">
                        {similarityRank.map(
                          (similarity: any, index: number) => (
                            <div
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                <span className="label">
                                  <span
                                    className="circulo"
                                    style={{
                                      backgroundColor:
                                        similarityColors[index + 1],
                                      fontWeight: 400,
                                    }}
                                  >
                                    &nbsp;&nbsp;&nbsp;&nbsp;
                                  </span>
                                  {wrappGetI18nGenre(similarity.key)}
                                </span>
                                <span className="value">
                                  {`${similarity.value} %`}
                                </span>
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </Col>
                  </Row>
                  <Row style={{ marginTop: "150px" }}>
                    <Col xs={24}>
                      <p style={{ textAlign: "center", fontSize: "22px" }}>
                        {t("pages.ArtistProfile.audioFeaturesExplanation")}
                      </p>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>
          </Row>
        </ExportProfilePage>
      </div>
    </Spin>
  );
};

export default ExportProfile;
